import React, { useState } from 'react';
import { Badge, Button, Image, Modal, Spin, Typography, message } from 'antd';
import { DownloadOutlined, LoadingOutlined, LeftOutlined, RightOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { downloadFile, downloadMultipleFiles, getFileNameFromUrl, isElectron } from '../utils/electronDownload';
import './TaskCard.css';

const { Text } = Typography;

interface TaskCardProps {
  taskId: string;
  taskName: string;
  status: 'processing' | 'success' | 'fail' | 'cleaned';
  resultInfo?: string;
  errorMsg?: string;
  createdAt: string;
  onUrlInvalid?: (taskId: string) => void; // 当检测到URL失效时的回调
  params?: Record<string, string> | null; // 任务创建时的参数摘要
}

const ERROR_FALLBACK_URL = 'https://saas.jochi.cn/storage/common/error_info.jpg';

const TaskCard: React.FC<TaskCardProps> = ({
  taskId,
  taskName,
  status,
  resultInfo,
  errorMsg,
  createdAt,
  onUrlInvalid,
  params
}) => {
  console.log('🔄 TaskCard 渲染:', { taskId, taskName, status, createdAt });

  const [currentPage, setCurrentPage] = useState(0);
  const [paramsModalVisible, setParamsModalVisible] = useState(false);

  // 解析结果信息
  const parseResultInfo = () => {
    if (!resultInfo) return null;
    
    try {
      // 尝试 JSON 解析（旧格式：{ urls: [...] }）
      const result = typeof resultInfo === 'string' ? JSON.parse(resultInfo) : resultInfo;
      return result;
    } catch {
      // 新格式：逗号分隔的 URL 字符串（apisource 返回）
      const urlList = (resultInfo as string).split(',').map(u => u.trim()).filter(Boolean);
      return { urls: urlList };
    }
  };
  
  const result = parseResultInfo();
  const urls = result?.urls || [];
  
  
  // 获取文件扩展名（兼容 URL 中含 ~ 后缀的情况，如 ...jpg~tplv-xxx）
  const getFileExtension = (url: string): string => {
    // 先去掉 query/hash，再取路径最后一段
    const path = url.split('?')[0].split('#')[0];
    const filename = path.split('/').pop() || '';
    // 匹配最后一个 . 后面到 ~ 或字符串结尾之间的部分
    const match = filename.match(/\.([a-zA-Z0-9]+)(?:[~]|$)/);
    return match ? match[1].toLowerCase() : '';
  };
  
  // 渲染媒体预览
  const renderMedia = (url: string, index: number) => {
    const ext = getFileExtension(url);
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext) || ext === '') {
      // 图片 - 自适应显示，保持原始比例
      return (
        <div key={index} className="media-item" style={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          overflow: 'hidden'
        }}>
          <Image
            src={url}
            alt={`result-${index}`}
            fallback={ERROR_FALLBACK_URL}
            style={{ 
              maxWidth: '100%',
              maxHeight: '400px',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',  // 保持比例，完整显示
              borderRadius: '8px'
            }}
            preview={{
              mask: '预览'
            }}
          />
        </div>
      );
    } else if (['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(ext)) {
      // 视频 - 自适应显示，保持原始比例
      return (
        <div key={index} className="media-item" style={{ 
          display: 'flex', 
          justifyContent: 'center',
          width: '100%'
        }}>
          <video
            controls
            style={{ 
              maxWidth: '100%',
              maxHeight: '400px',
              width: 'auto',
              height: 'auto',
              borderRadius: '8px',
              backgroundColor: '#000'
            }}
            preload="metadata"
          >
            <source src={url} type={`video/${ext}`} />
            您的浏览器不支持视频播放
          </video>
        </div>
      );
    } else if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(ext)) {
      // 音频
      return (
        <div key={index} className="media-item">
          <audio
            controls
            style={{ width: '100%' }}
            preload="metadata"
          >
            <source src={url} type={`audio/${ext}`} />
            您的浏览器不支持音频播放
          </audio>
        </div>
      );
    } else {
      // 其他文件，显示下载链接
      return (
        <div key={index} className="media-item">
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url.split('/').pop()}
          </a>
        </div>
      );
    }
  };
  
  // 下载所有文件 - 支持Electron和浏览器环境
  const handleDownloadAll = async () => {
    if (urls.length === 0) {
      message.warning('没有可下载的文件');
      return;
    }
    
    const electronEnv = isElectron();
    console.log(`🔧 检测到环境: ${electronEnv ? 'Electron' : '浏览器'}`);
    
    // 显示下载提示
    if (urls.length === 1) {
      message.info({
        content: electronEnv ? '请选择保存位置' : '开始下载文件',
        duration: 2
      });
    } else {
      message.info({
        content: electronEnv ? `请选择保存文件夹 (${urls.length} 个文件)` : `准备下载 ${urls.length} 个文件`,
        duration: 3
      });
    }
    
    // 显示加载状态
    message.loading({
      content: `正在下载 ${urls.length} 个文件...`,
      key: 'download',
      duration: 0
    });
    
    try {
      let result;
      
      console.log(`🔄 开始下载: ${urls.length} 个文件`);
      console.log('📋 文件列表:', urls);
      
      if (urls.length === 1) {
        // 单文件下载 - 在Electron环境下也要弹出保存对话框
        const url = urls[0];
        const fileName = getFileNameFromUrl(url, taskName);
        
        console.log(`📥 单文件下载: ${fileName}`);
        
        const success = await downloadFile(url, fileName);
        result = {
          success: success ? 1 : 0,
          total: 1,
          errors: success ? [] : ['下载失败']
        };
        
        console.log(`📊 单文件下载结果: ${success ? '成功' : '失败'}`);
      } else {
        // 批量下载
        console.log(`📦 批量下载: ${urls.length} 个文件`);
        result = await downloadMultipleFiles(urls, taskName);
        console.log(`📊 批量下载结果: ${result.success}/${result.total} 成功`);
      }
      
      // 显示结果
      if (result.success === result.total) {
        message.success({
          content: `✅ 成功下载 ${result.success} 个文件`,
          key: 'download',
          duration: 3
        });
      } else if (result.success > 0) {
        message.warning({
          content: `⚠️ 成功下载 ${result.success}/${result.total} 个文件`,
          key: 'download',
          duration: 5
        });
        
        // 显示错误详情
        if (result.errors.length > 0) {
          console.error('下载错误详情:', result.errors);
        }
      } else {
        message.error({
          content: `❌ 下载失败`,
          key: 'download',
          duration: 5
        });
        
        if (result.errors.length > 0) {
          console.error('下载失败原因:', result.errors);
        }
      }
      
    } catch (error) {
      message.error({
        content: '下载过程中发生错误，请重试',
        key: 'download'
      });
      console.error('下载异常:', error);
    }
  };
  
  return (
    <div 
      className="task-card-modern"
      style={{ 
        padding: '16px',
        background: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e8e8e8',
        maxWidth: '460px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}
    >
      {/* 标题栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: params ? '6px' : '12px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ 
              width: '6px', 
              height: '6px', 
              borderRadius: '50%', 
              background: status === 'processing' ? '#1890ff' : status === 'success' ? '#52c41a' : status === 'cleaned' ? '#faad14' : '#ff4d4f',
              display: 'inline-block'
            }} />
            <Text strong style={{ fontSize: '14px', color: '#262626' }}>{taskName}</Text>
          </div>
        </div>
        <Badge 
          status={status === 'processing' ? 'processing' : status === 'success' ? 'success' : status === 'cleaned' ? 'warning' : 'error'} 
          text={
            <span style={{ 
              fontSize: '11px', 
              fontWeight: 500,
              color: status === 'processing' ? '#1890ff' : status === 'success' ? '#52c41a' : status === 'cleaned' ? '#faad14' : '#ff4d4f'
            }}>
              {status === 'processing' ? 'PROCESSING' : status === 'success' ? 'COMPLETED' : status === 'cleaned' ? 'CLEANED' : 'FAILED'}
            </span>
          }
        />
      </div>
      
      {/* 参数详情链接 - 仅当有 params 时显示 */}
      {params && Object.keys(params).length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <span
            onClick={() => setParamsModalVisible(true)}
            style={{ 
              fontSize: '12px', 
              color: '#1890ff', 
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <UnorderedListOutlined style={{ fontSize: '11px' }} />
            参数详情
          </span>
        </div>
      )}

      {/* 参数详情 Modal */}
      <Modal
        title="任务参数详情"
        open={paramsModalVisible}
        onCancel={() => setParamsModalVisible(false)}
        footer={<Button onClick={() => setParamsModalVisible(false)}>关闭</Button>}
        width={480}
      >
        {params && (
          <div style={{ padding: '4px 0' }}>
            {Object.entries(params).map(([key, value]) => (
              <div key={key} style={{ marginBottom: '12px' }}>
                <div style={{ fontWeight: 600, color: '#1890ff', fontSize: '13px', marginBottom: '4px' }}>
                  {key}:
                </div>
                <div style={{ 
                  paddingLeft: '12px', 
                  color: '#595959', 
                  fontSize: '13px',
                  wordBreak: 'break-all',
                  lineHeight: '1.6'
                }}>
                  {value}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
      
      {/* 处理中状态 */}
      {status === 'processing' && (
        <div style={{ 
          padding: '32px 0', 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          gap: '12px',
          background: '#fafafa',
          borderRadius: '8px'
        }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: '#1890ff' }} spin />} />
          <Text type="secondary" style={{ fontSize: '13px' }}>任务正在执行中，请耐心等待...</Text>
        </div>
      )}
      
      {/* 成功状态 - 显示结果（分页） */}
      {status === 'success' && urls.length > 0 && (
        <div>
          {/* 主要内容区域 */}
          <div style={{ 
            background: '#fafafa', 
            borderRadius: '8px', 
            padding: '12px',
            marginBottom: '12px',
            position: 'relative'
          }}>
            {/* 图片/视频展示 */}
            <div style={{ position: 'relative' }}>
              {renderMedia(urls[currentPage], currentPage)}
              
              {/* 分页指示器 */}
              {urls.length > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '12px'
                }}>
                  <Button 
                    type="text" 
                    size="small"
                    icon={<LeftOutlined />}
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    style={{ minWidth: '32px', height: '32px' }}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {urls.map((_url: string, index: number) => (
                      <div
                        key={index}
                        onClick={() => setCurrentPage(index)}
                        role="button"
                        tabIndex={0}
                        aria-label={`切换到第 ${index + 1} 页`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            setCurrentPage(index);
                          }
                        }}
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: index === currentPage ? '#1890ff' : '#d9d9d9',
                          cursor: 'pointer',
                          transition: 'all 0.3s'
                        }}
                      />
                    ))}
                  </div>
                  <Button 
                    type="text" 
                    size="small"
                    icon={<RightOutlined />}
                    disabled={currentPage === urls.length - 1}
                    onClick={() => setCurrentPage(prev => Math.min(urls.length - 1, prev + 1))}
                    style={{ minWidth: '32px', height: '32px' }}
                  />
                </div>
              )}
            </div>
            
            {/* 页码信息 */}
            {urls.length > 1 && (
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {currentPage + 1} / {urls.length}
                </Text>
              </div>
            )}
          </div>
          
          {/* 底部操作栏 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Task: {taskId.substring(0, 16)}...
            </Text>
            <Button
              type="primary"
              icon={<DownloadOutlined style={{ color: 'white' }} />}
              size="small"
              onClick={handleDownloadAll}
              style={{ 
                background: 'linear-gradient(to right, #4F46E5, #7C3AED)',
                borderColor: 'transparent',
                borderRadius: '6px',
                fontSize: '12px',
                height: '32px',
                color: 'white'
              }}
            >
              <span style={{ color: 'white' }}>Download {urls.length > 1 ? `(${urls.length})` : ''}</span>
            </Button>
          </div>
        </div>
      )}
      
      {/* 失败状态 - 显示错误信息 */}
      {status === 'fail' && (
        <div style={{ 
          padding: '12px', 
          background: '#fff1f0', 
          borderRadius: '8px', 
          fontSize: '13px', 
          color: '#cf1322',
          border: '1px solid #ffccc7'
        }}>
          <div style={{ fontWeight: 500, marginBottom: '4px' }}>Error:</div>
          <div style={{ 
            maxHeight: '120px', 
            overflowY: 'auto', 
            wordBreak: 'break-all',
            whiteSpace: 'pre-wrap',
            lineHeight: '1.5'
          }}>{errorMsg || '任务执行失败'}</div>
        </div>
      )}
      
      {/* 已清理状态 - 显示提示信息 */}
      {status === 'cleaned' && (
        <div style={{ 
          padding: '24px 12px', 
          background: '#fffbe6', 
          borderRadius: '8px', 
          fontSize: '13px', 
          color: '#ad6800',
          border: '1px solid #ffe58f',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>🗑️</div>
          <div style={{ fontWeight: 500, marginBottom: '4px' }}>任务结果已清理</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
            生成的文件已被系统定期清理，无法再查看
          </div>
        </div>
      )}
    </div>
  );
};

// 使用 React.memo 优化性能，避免不必要的重新渲染
export default React.memo(TaskCard);
